/**
 * health/ProviderHealth.ts — track how each provider is behaving over time.
 *
 * The engine records every attempt (success/failure, latency) so it can rank
 * providers, prefer healthy ones, and expose provenance to future engines.
 */

export interface ProviderHealthRecord {
  providerId: string;
  successes: number;
  failures: number;
  consecutiveFailures: number;
  lastError: string | null;
  lastLatencyMs: number | null;
  avgLatencyMs: number | null;
  lastUsedAt: number | null;
  /** 0–100 rolling health. */
  healthScore: number;
}

export class ProviderHealthRegistry {
  private records = new Map<string, ProviderHealthRecord>();

  private ensure(providerId: string): ProviderHealthRecord {
    let rec = this.records.get(providerId);
    if (!rec) {
      rec = {
        providerId,
        successes: 0,
        failures: 0,
        consecutiveFailures: 0,
        lastError: null,
        lastLatencyMs: null,
        avgLatencyMs: null,
        lastUsedAt: null,
        healthScore: 100,
      };
      this.records.set(providerId, rec);
    }
    return rec;
  }

  recordSuccess(providerId: string, latencyMs: number, now: number): void {
    const rec = this.ensure(providerId);
    rec.successes += 1;
    rec.consecutiveFailures = 0;
    rec.lastError = null;
    rec.lastLatencyMs = latencyMs;
    rec.avgLatencyMs = rec.avgLatencyMs == null ? latencyMs : Math.round(rec.avgLatencyMs * 0.7 + latencyMs * 0.3);
    rec.lastUsedAt = now;
    rec.healthScore = this.computeScore(rec);
  }

  recordFailure(providerId: string, error: string, now: number): void {
    const rec = this.ensure(providerId);
    rec.failures += 1;
    rec.consecutiveFailures += 1;
    rec.lastError = error;
    rec.lastUsedAt = now;
    rec.healthScore = this.computeScore(rec);
  }

  private computeScore(rec: ProviderHealthRecord): number {
    const total = rec.successes + rec.failures;
    if (total === 0) return 100;
    const successRate = rec.successes / total;
    // Recent consecutive failures hurt more than the lifetime ratio.
    const penalty = Math.min(40, rec.consecutiveFailures * 15);
    return Math.max(0, Math.round(successRate * 100 - penalty));
  }

  get(providerId: string): ProviderHealthRecord | null {
    const rec = this.records.get(providerId);
    return rec ? { ...rec } : null;
  }

  list(): ProviderHealthRecord[] {
    return Array.from(this.records.values()).map((r) => ({ ...r }));
  }

  clear(): void {
    this.records.clear();
  }
}
